import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { CHANNELS_WITH_METRICS } from 'utils/channels';

import { getShortlistedUISettings } from 'selectors/participants';

import { danger } from 'redux-store/reducers/common-reducer';
import { AVAILABLE_COLUMNS_FOR_SHORLISTED, saveCampaignUISettingsShorlisted } from 'modules/campaigns/reducers/campaignUISettingsShorlisted';

import type { CampaignColumnsVisibility, CampaignParticipantsColumnsVisibilityState } from 'types';

import './subcampaigns-table-shortlisted-customization-modal.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./subcampaigns-table-shortlisted-customization-modal.i18n.json'));

type OwnProps = {
  campaignId: number,
  onCloseModal: () => void
};

type StateProps = {
  campaignUISettingsShorlisted: CampaignParticipantsColumnsVisibilityState,
  campaignUISettingsShorlistedDefault: CampaignParticipantsColumnsVisibilityState
};

type DispatchProps = {
  dispatchSaveCampaignUISettingsShorlisted: (b: number, a: CampaignColumnsVisibility) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  checkboxStates: CampaignParticipantsColumnsVisibilityState
};

type EntryType = {
  id: string,
  label: string
};

class SubcampaignsTableShortlistedCustomizationModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      checkboxStates: this.props.campaignUISettingsShorlisted
    };
  }

  // will be rendered left-to-right top-to-bottom in 3 columns
  getColumns = (): Array<EntryType> => AVAILABLE_COLUMNS_FOR_SHORLISTED.map(column => ({
    id: column,
    label: __t(`subcampaigns_table_shortlisted_customization_modal.columns.${column}`)
  }));

  getChannels = (): Array<EntryType> => CHANNELS_WITH_METRICS.map(channel => ({
    id: channel,
    label: __t(`subcampaigns_table_shortlisted_customization_modal.channels.${channel}`)
  }));

  handleToggle = (event: SyntheticEvent<HTMLInputElement>) => {
    const { checkboxStates } = this.state;
    const name = event.currentTarget.name;
    const visible = event.currentTarget.checked;
    const group = event.currentTarget.dataset.group;

    const isOnlyOneChannelSelected = [
      checkboxStates.channels.instagram,
      checkboxStates.channels.tiktok,
      checkboxStates.channels.youtube
    ].filter(isDefined).length === 1;

    if (group === 'channels' && isOnlyOneChannelSelected && !visible) {
      // do not allow deselecting all
      return;
    }

    this.setState((state) => ({
      checkboxStates: {
        ...state.checkboxStates,
        [String(group)]: {
          ...state.checkboxStates[String(group)],
          [name]: visible
        }
      }
    }));
  };

  handleSavePreferences = () => {
    const { campaignId, dispatchSaveCampaignUISettingsShorlisted, onCloseModal } = this.props;
    const { checkboxStates } = this.state;

    const channels: Array<string> = Object.keys(checkboxStates.channels);
    const columns: Array<string> = Object.keys(checkboxStates.columns);

    const data: CampaignColumnsVisibility = {
      channels: channels.map(type => ({
        type,
        visible: checkboxStates.channels[type]
      })),
      columns: columns.map(type => ({
        type,
        visible: checkboxStates.columns[type]
      }))
    };

    dispatchSaveCampaignUISettingsShorlisted(campaignId, data);
    onCloseModal();
  };

  handleResetToDefault = (): void => {
    this.setState({
      checkboxStates: this.props.campaignUISettingsShorlistedDefault
    });
  };

  renderItems = (group: string, items: Array<EntryType>): Array<JSX.Element> => {
    const { checkboxStates } = this.state;

    return items.map(item => (
      <InputCheckbox
        key={`subcampaigns-table-shortlisted-customization-modal__checkbox-${group}-${item.id}`}
        id={`subcampaigns-table-shortlisted-customization-modal__checkbox-${group}-${item.id}`}
        name={item.id}
        label={item.label}
        value={item.id}
        data={{ group }}
        checked={checkboxStates[group][item.id] || false}
        onChange={this.handleToggle}
      />
    ));
  };

  renderReset = () => (
    <div className="subcampaigns-table-shortlisted-customization-modal__reset">
      <div onClick={this.handleResetToDefault}>
        {__t('subcampaigns_table_shortlisted_customization_modal.reset_to_default')}
      </div>
    </div>
  );

  renderGroup = (group: string) => (
    <div className="subcampaigns-table-shortlisted-customization-modal__group">
      <div className="subcampaigns-table-shortlisted-customization-modal__group-header">
        {__t(`subcampaigns_table_shortlisted_customization_modal.${group}_subtitle`)}
      </div>
      <div className="subcampaigns-table-shortlisted-customization-modal__checkboxes">
        {this.renderItems(group, group === 'channels' ? this.getChannels() : this.getColumns())}
      </div>
    </div>
  );

  render() {
    const { onCloseModal } = this.props;

    return (
      <ModalFrame
        title={__t('subcampaigns_table_shortlisted_customization_modal.modal_title')}
      >
        <ModalFrameInner align="left">
          {this.renderGroup('channels')}
          {this.renderGroup('columns')}
          {this.renderReset()}
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            newTab
            onClick={onCloseModal}
          >
            {__t('collabary_common.action_button.cancel')}
          </ActionButton>
          <ActionButton
            color="primary"
            onClick={this.handleSavePreferences}
          >
            {__t('collabary_common.action_button.save')}
          </ActionButton>
        </ModalFrameFooter>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ campaignUISettingsShorlisted }): StateProps => ({
  campaignUISettingsShorlisted: getShortlistedUISettings(campaignUISettingsShorlisted.visibility),
  campaignUISettingsShorlistedDefault: getShortlistedUISettings(campaignUISettingsShorlisted.visibilityDefault)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveCampaignUISettingsShorlisted: (campaignId: number, data: CampaignColumnsVisibility) =>
    dispatch(saveCampaignUISettingsShorlisted(campaignId, data))
      .catch(() => dispatch(danger(__t('subcampaigns_table_shortlisted_customization_modal.ui_settings_save_error'))))
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignsTableShortlistedCustomizationModal);
